import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useId,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowPathIcon,
  EnvelopeIcon,
  CloudArrowUpIcon,
  QuestionMarkCircleIcon,
  NewspaperIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline'
import { getSettings, updateSetting } from '@/services/settings'
import type { Setting } from '@/types/settings'
import { getCategories, type QBOCategory } from '@/services/qbo'
import { filterCreditCardCategories } from '@/utils/amazonSettings'
import VendorAutoCategorizationExclusions from '@/components/VendorAutoCategorizationExclusions'
import SharedCostKeywordsEditor from '@/components/SharedCostKeywordsEditor'
import { SHARED_COST_KEYWORDS_KEY } from '@/utils/sharedCostKeywords'
import { toast } from '@/store/toastStore'

const AUTO_CATEGORIZE_EXCLUDED_VENDOR_IDS_KEY = 'auto_categorize_excluded_vendor_ids'

/** Shared style for in-section headings (Monitored inboxes, Sync from QuickBooks, etc.). */
const settingsSubsectionTitleClass =
  'container-title leading-snug tracking-tight'

function HelpDetailPopover({
  'aria-label': ariaLabel,
  align = 'start',
  children,
}: {
  'aria-label': string
  align?: 'start' | 'end'
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const [panelBox, setPanelBox] = useState<{
    top: number
    left: number
    width: number
  } | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null)
      return
    }
    function compute() {
      const btn = buttonRef.current
      if (!btn) return
      const rect = btn.getBoundingClientRect()
      const padding = 12
      const vw = window.innerWidth
      const width = Math.min(520, Math.max(300, vw - padding * 2))
      let left = align === 'end' ? rect.right - width : rect.left
      left = Math.max(padding, Math.min(left, vw - width - padding))
      setPanelBox({ top: rect.bottom + 8, left, width })
    }
    compute()
    window.addEventListener('scroll', compute, true)
    window.addEventListener('resize', compute)
    return () => {
      window.removeEventListener('scroll', compute, true)
      window.removeEventListener('resize', compute)
    }
  }, [open, align])

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      const t = e.target as Node
      if (wrapRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    window.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  const panel =
    open && panelBox && typeof document !== 'undefined' ? (
      <div
        ref={panelRef}
        id={panelId}
        role="region"
        aria-label={ariaLabel}
        style={{
          position: 'fixed',
          top: panelBox.top,
          left: panelBox.left,
          width: panelBox.width,
        }}
        className="z-[200] max-h-[min(70vh,28rem)] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-600 shadow-xl ring-1 ring-black/10"
      >
        <div className="space-y-2 [&_strong]:font-semibold [&_strong]:text-gray-800">{children}</div>
      </div>
    ) : null

  return (
    <div
      className={`relative inline-flex flex-shrink-0 ${align === 'end' ? 'justify-end' : ''}`}
      ref={wrapRef}
    >
      <button
        ref={buttonRef}
        type="button"
        className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200/60 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50 focus-visible:ring-offset-1"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden />
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </div>
  )
}

function SettingsSubsectionHeadingWithHelp({
  title,
  'aria-label': ariaLabel,
  children,
}: {
  title: string
  'aria-label': string
  children: ReactNode
}) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <h3 className={settingsSubsectionTitleClass}>{title}</h3>
      <HelpDetailPopover aria-label={ariaLabel} align="start">
        {children}
      </HelpDetailPopover>
    </div>
  )
}

function SettingsSection({
  title,
  detail,
  icon,
  children,
  grouped = false,
}: {
  title: string
  detail?: ReactNode
  icon?: ReactNode
  children: ReactNode
  /** When true, omit outer card chrome; use inside a parent with divide-y + rounded-2xl border. */
  grouped?: boolean
}) {
  return (
    <section className={grouped ? 'bg-white' : 'rounded-xl border border-gray-200 bg-white shadow-sm shadow-gray-950/[0.04]'}>
      <div
        className={
          grouped
            ? 'px-5 py-3.5 sm:px-8 sm:py-4 bg-gray-50/80'
            : 'border-b border-gray-100 bg-gradient-to-b from-gray-50/90 to-gray-50/30 px-6 py-4'
        }
      >
        <div className="flex items-center gap-3">
          {icon ? <div className="flex-shrink-0">{icon}</div> : null}
          <div className="flex min-w-0 flex-1 items-center justify-between gap-2">
            <h2 className="text-base font-semibold leading-snug text-gray-900 tracking-tight">{title}</h2>
            {detail ? (
              <HelpDetailPopover aria-label={`Learn more: ${title}`} align="end">
                {detail}
              </HelpDetailPopover>
            ) : null}
          </div>
        </div>
      </div>
      <div className={grouped ? 'px-5 py-6 sm:px-8 sm:py-7' : 'p-6 sm:p-7 lg:p-8'}>{children}</div>
    </section>
  )
}

function SettingRow({
  label,
  badge,
  children,
}: {
  label: string
  badge?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-start sm:gap-6">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-900">{label}</span>
        {badge}
      </div>
      <div className="flex shrink-0">{children}</div>
    </div>
  )
}

/** Compact card for scheduled Gmail integrations (QBO ingest vs Meez forward). */
function ForwardingIntegrationCard({
  title,
  subtitle,
  active,
  toggle,
  children,
}: {
  title: string
  subtitle: string
  active: boolean
  toggle: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border border-gray-200/90 bg-white p-4 shadow-sm shadow-gray-950/[0.03] ring-1 ring-gray-950/[0.02] sm:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="container-title tracking-tight">{title}</h3>
            <span
              className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${
                active
                  ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100'
                  : 'bg-gray-50 text-gray-600 ring-1 ring-gray-100'
              }`}
            >
              {active ? 'On' : 'Off'}
            </span>
          </div>
          <p className="text-xs leading-relaxed text-gray-500">{subtitle}</p>
        </div>
        <div className="flex shrink-0 items-center sm:pt-0.5">{toggle}</div>
      </div>
      {active && children != null ? (
        <div className="mt-5 flex min-h-0 flex-1 flex-col border-t border-gray-100 pt-5">{children}</div>
      ) : (
        <div className="min-h-0 flex-1" aria-hidden />
      )}
    </div>
  )
}

export default function Settings() {
  const [settings, setSettings] = useState<Record<string, Setting>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [qboCategories, setQboCategories] = useState<QBOCategory[]>([])
  const originalValueRef = useRef<string | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  useEffect(() => {
    let cancelled = false
    void getCategories()
      .then((cats) => {
        if (!cancelled) setQboCategories(cats)
      })
      .catch((err) => {
        console.error(err)
        if (!cancelled) toast.error('Failed to load QuickBooks accounts')
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function loadSettings() {
    try {
      setLoading(true)
      const data = await getSettings()
      setSettings(data.settings)
    } catch (err) {
      toast.error('Failed to load settings')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function handleSave(key: string, value: string) {
    try {
      setSaving(key)
      const updated = await updateSetting(key, { value })
      setSettings((prev) => ({ ...prev, [key]: updated }))
    } catch (err) {
      toast.error(`Failed to save ${key}`)
      console.error(err)
    } finally {
      setSaving(null)
    }
  }

  function getValue(key: string): string {
    return settings[key]?.value || ''
  }

  function isActive(key: string): boolean {
    const val = getValue(key).toLowerCase()
    return val === 'true' || val === '1'
  }

  function onBadge(active: boolean) {
    return (
      <span
        className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${
          active ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100' : 'bg-gray-50 text-gray-600 ring-1 ring-gray-100'
        }`}
      >
        {active ? 'On' : 'Off'}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full items-center justify-center">
        <div className="flex items-center gap-3 text-gray-500">
          <ArrowPathIcon className="w-5 h-5 animate-spin" />
          <span>Loading settings...</span>
        </div>
      </div>
    )
  }

  const emailSectionIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100/90 ring-1 ring-violet-200/50">
      <EnvelopeIcon className="h-5 w-5 text-violet-700" />
    </div>
  )
  const forwardingSectionIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-100/90 ring-1 ring-sky-200/50">
      <CloudArrowUpIcon className="h-5 w-5 text-sky-700" />
    </div>
  )
  const dailySummarySectionIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100/90 ring-1 ring-rose-200/50">
      <NewspaperIcon className="h-5 w-5 text-rose-700" />
    </div>
  )
  const amazonSectionIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100/90 ring-1 ring-orange-200/50">
      <ShoppingBagIcon className="h-5 w-5 text-orange-800" />
    </div>
  )

  const creditCardAccounts = filterCreditCardCategories(qboCategories)
  const savedAmazonCardId = getValue('amazon_card_account_id')

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4 pb-8">
      <SettingsSection
        title="Email review settings"
        icon={emailSectionIcon}
        detail={
            <>
              <p>
                Tell Bookkeeper which Gmail accounts to sign in to for watches, email rules, and scheduled
                jobs; optionally which address the Doc Review import should look for; optional inbox triage (filing
                stale rule-matched threads to labels); vendors to exclude from auto-categorization; and shared-cost
                keyword phrases for invoice line classification.
              </p>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start lg:gap-10">
            <div className="min-w-0 space-y-5 lg:pr-2">
              <SettingsSubsectionHeadingWithHelp
                title="Doc review inbox"
                aria-label="About the Doc review inbox alias"
              >
                <p>
                  Optional. For Google Workspace, enter the full invoice address the nightly Doc Review import should
                  look for—often a group or alias such as <strong>invoices@company.com</strong>.
                </p>
                <p>
                  The job uses the same Gmail search shape as Email search when you use <strong>Has the words</strong>{' '}
                  with that address in quotes, so only messages that mention the address in headers or body are pulled.
                  It does not import everything visible in a shared primary inbox.
                </p>
                <p>
                  Mail that only hits your personal address still appears when a <strong>confirmed email rule</strong>{' '}
                  with QuickBooks enabled matches it.
                </p>
                <p>Clear the field to turn off scheduled Doc Review ingestion.</p>
              </SettingsSubsectionHeadingWithHelp>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5" htmlFor="settings-internal-invoice-alias">
                  Alias address
                </label>
                <input
                  id="settings-internal-invoice-alias"
                  type="email"
                  className="input w-full text-sm font-mono"
                  placeholder="invoices@company.com"
                  value={getValue('internal_invoice_ingest_alias')}
                  onFocus={(e) => {
                    originalValueRef.current = e.target.value
                  }}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      internal_invoice_ingest_alias: {
                        ...prev['internal_invoice_ingest_alias'],
                        value: e.target.value,
                      } as Setting,
                    }))
                  }
                  onBlur={(e) => {
                    if (e.target.value !== originalValueRef.current) {
                      handleSave('internal_invoice_ingest_alias', e.target.value)
                    }
                    originalValueRef.current = null
                  }}
                  disabled={saving === 'internal_invoice_ingest_alias'}
                />
              </div>
            </div>

            <div className="min-w-0 space-y-5 border-t border-gray-100 pt-8 lg:border-l lg:border-t-0 lg:border-gray-200 lg:pl-10 lg:pt-0">
              <SettingsSubsectionHeadingWithHelp
                title="Monitored inboxes"
                aria-label="About monitored inboxes"
              >
                <p>
                  Enter one address per line. These are the mailboxes Bookkeeper uses when running Gmail watch
                  and background jobs (for example QuickBooks ingest and Meez forwarding).
                </p>
                <p>Each address must be an inbox your deployment can authenticate to.</p>
              </SettingsSubsectionHeadingWithHelp>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5" htmlFor="settings-user-emails">
                  Email addresses (one per line)
                </label>
                <textarea
                  id="settings-user-emails"
                  className="input min-h-[8.5rem] w-full text-sm font-mono sm:min-h-[9rem]"
                  rows={4}
                  value={getValue('user_emails')}
                  onFocus={(e) => {
                    originalValueRef.current = e.target.value
                  }}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      user_emails: { ...prev['user_emails'], value: e.target.value } as Setting,
                    }))
                  }
                  onBlur={(e) => {
                    if (e.target.value !== originalValueRef.current) {
                      handleSave('user_emails', e.target.value)
                    }
                    originalValueRef.current = null
                  }}
                  disabled={saving === 'user_emails'}
                  placeholder={'ap@company.com\ninvoices@company.com'}
                />
              </div>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-200/90 pt-8">
            <SettingsSubsectionHeadingWithHelp
              title="Gmail inbox triage"
              aria-label="About Gmail inbox triage"
            >
              <p>
                After the nightly job ingests invoice mail, this can move matching threads out of Inbox into a
                Gmail label when the thread has had no activity for the number of days you set below.
              </p>
              <p>
                It uses the same <strong>confirmed email rules</strong> with <strong>QuickBooks</strong> as the
                scheduled ingest. Turn on <strong>Dry run</strong> first to log actions without changing labels in
                Gmail.
              </p>
              <p>
                Each monitored inbox must appear in the label map with the exact label name that already exists in
                that account (nested labels use <strong>/</strong> in the name).
              </p>
            </SettingsSubsectionHeadingWithHelp>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start lg:gap-10">
              <div className="min-w-0 space-y-5 lg:pr-2">
                <SettingRow label="Enable inbox triage" badge={onBadge(isActive('gmail_triage_active'))}>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={isActive('gmail_triage_active')}
                      onChange={(e) =>
                        handleSave('gmail_triage_active', e.target.checked ? 'true' : 'false')
                      }
                      disabled={saving === 'gmail_triage_active'}
                    />
                    <span className="toggle-slider" />
                  </label>
                </SettingRow>

                <SettingRow label="Dry run (log only)" badge={onBadge(isActive('gmail_triage_dry_run'))}>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={isActive('gmail_triage_dry_run')}
                      onChange={(e) =>
                        handleSave('gmail_triage_dry_run', e.target.checked ? 'true' : 'false')
                      }
                      disabled={saving === 'gmail_triage_dry_run'}
                    />
                    <span className="toggle-slider" />
                  </label>
                </SettingRow>

                <div>
                  <label
                    className="mb-1.5 block text-xs font-medium text-gray-600"
                    htmlFor="settings-gmail-triage-age-days"
                  >
                    Min days without thread activity before archiving
                  </label>
                  <input
                    id="settings-gmail-triage-age-days"
                    type="number"
                    min={1}
                    max={365}
                    className="input w-full max-w-[11rem] text-sm"
                    value={getValue('gmail_triage_min_thread_age_days') || '7'}
                    onFocus={(e) => {
                      originalValueRef.current = e.target.value
                    }}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        gmail_triage_min_thread_age_days: {
                          ...(prev['gmail_triage_min_thread_age_days'] ?? {}),
                          value: e.target.value,
                        } as Setting,
                      }))
                    }
                    onBlur={(e) => {
                      if (e.target.value !== originalValueRef.current) {
                        handleSave('gmail_triage_min_thread_age_days', e.target.value || '7')
                      }
                      originalValueRef.current = null
                    }}
                    disabled={saving === 'gmail_triage_min_thread_age_days'}
                  />
                </div>
              </div>

              <div className="min-w-0 space-y-0 border-t border-gray-100 pt-8 lg:border-l lg:border-t-0 lg:border-gray-200 lg:pl-10 lg:pt-0">
                <label
                  className="mb-1.5 block text-xs font-medium text-gray-600"
                  htmlFor="settings-gmail-triage-label-map"
                >
                  Label map (JSON)
                </label>
                <p className="mb-1.5 text-xs leading-relaxed text-gray-500">
                  Object mapping each monitored inbox address to the Gmail label used when filing threads out of
                  Inbox. Inboxes omitted here are skipped by triage.
                </p>
                <textarea
                  id="settings-gmail-triage-label-map"
                  className="input min-h-[12rem] w-full text-sm font-mono sm:min-h-[14rem]"
                  rows={8}
                  placeholder={
                    '{\n' +
                    '  "taylor@thecottagegrocery.com": "0 - Cottage Grocery/Invoices",\n' +
                    '  "lauren@thecottagegrocery.com": "Vendors"\n' +
                    '}'
                  }
                  value={getValue('gmail_triage_label_map')}
                  onFocus={(e) => {
                    originalValueRef.current = e.target.value
                  }}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      gmail_triage_label_map: {
                        ...(prev['gmail_triage_label_map'] ?? {}),
                        value: e.target.value,
                      } as Setting,
                    }))
                  }
                  onBlur={(e) => {
                    if (e.target.value !== originalValueRef.current) {
                      handleSave('gmail_triage_label_map', e.target.value)
                    }
                    originalValueRef.current = null
                  }}
                  disabled={saving === 'gmail_triage_label_map'}
                />
              </div>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-200/90 pt-8">
            <h3 className={`${settingsSubsectionTitleClass} mb-4`}>Vendors without auto-categorization</h3>
            <VendorAutoCategorizationExclusions
              excludedVendorIdsRaw={getValue(AUTO_CATEGORIZE_EXCLUDED_VENDOR_IDS_KEY)}
              saving={saving === AUTO_CATEGORIZE_EXCLUDED_VENDOR_IDS_KEY}
              onSave={(value) => handleSave(AUTO_CATEGORIZE_EXCLUDED_VENDOR_IDS_KEY, value)}
            />
          </div>

          <div className="mt-8 border-t border-gray-200/90 pt-8">
            <h3 className={`${settingsSubsectionTitleClass} mb-4`}>Shared cost keywords</h3>
            <SharedCostKeywordsEditor
              keywordsRaw={getValue(SHARED_COST_KEYWORDS_KEY)}
              saving={saving === SHARED_COST_KEYWORDS_KEY}
              onSave={(value) => handleSave(SHARED_COST_KEYWORDS_KEY, value)}
            />
          </div>
        </SettingsSection>

      <SettingsSection
        title="Forwarding settings"
        icon={forwardingSectionIcon}
        detail={
            <>
              <p>
                These switches control whether scheduled jobs run each integration. When an integration is on,
                Bookkeeper searches your <strong>monitored inboxes</strong> using <strong>confirmed email rules</strong>{' '}
                that target that destination.
              </p>
              <p>
                <strong>QuickBooks</strong> runs the ingest pipeline: matching messages become documents for review
                and posting.
              </p>
              <p>
                <strong>Meez</strong> forwards matching messages to the Meez address you configure.
              </p>
              <p>
                <strong>Lookback days</strong> sets how far back each job searches your monitored inboxes.
                QuickBooks ingest and the optional Doc Review alias import use the QuickBooks lookback; Meez uses its
                own lookback when forwarding.
              </p>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-stretch lg:gap-6">
            <ForwardingIntegrationCard
              title="QuickBooks (Doc Review ingest)"
              subtitle="Pulls messages that match confirmed email rules with QuickBooks as a destination, then creates documents for review—not email forwarding to QuickBooks."
              active={isActive('qbo_active')}
              toggle={
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={isActive('qbo_active')}
                    onChange={(e) => handleSave('qbo_active', e.target.checked ? 'true' : 'false')}
                    disabled={saving === 'qbo_active'}
                  />
                  <span className="toggle-slider" />
                </label>
              }
            >
              <div className="max-w-[11rem]">
                <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="settings-qbo-lookback">
                  Lookback days
                </label>
                <input
                  id="settings-qbo-lookback"
                  type="number"
                  className="input w-full text-sm"
                  placeholder="7"
                  min="1"
                  max="90"
                  value={getValue('qbo_lookback_days')}
                  onFocus={(e) => {
                    originalValueRef.current = e.target.value
                  }}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      qbo_lookback_days: { ...prev['qbo_lookback_days'], value: e.target.value } as Setting,
                    }))
                  }
                  onBlur={(e) => {
                    if (e.target.value !== originalValueRef.current) {
                      handleSave('qbo_lookback_days', e.target.value)
                    }
                    originalValueRef.current = null
                  }}
                  disabled={saving === 'qbo_lookback_days'}
                />
              </div>
            </ForwardingIntegrationCard>

            <ForwardingIntegrationCard
              title="Meez"
              subtitle="Forwards matching rule mail to your Meez inbox so recipes and costs can sync there."
              active={isActive('meez_active')}
              toggle={
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={isActive('meez_active')}
                    onChange={(e) => handleSave('meez_active', e.target.checked ? 'true' : 'false')}
                    disabled={saving === 'meez_active'}
                  />
                  <span className="toggle-slider" />
                </label>
              }
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_6.75rem] sm:items-end sm:gap-x-5">
                <div className="min-w-0">
                  <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="settings-meez-email">
                    Destination email
                  </label>
                  <input
                    id="settings-meez-email"
                    type="email"
                    className="input w-full text-sm"
                    placeholder="invoices@meez.com"
                    value={getValue('meez_email')}
                    onFocus={(e) => {
                      originalValueRef.current = e.target.value
                    }}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        meez_email: { ...prev['meez_email'], value: e.target.value } as Setting,
                      }))
                    }
                    onBlur={(e) => {
                      if (e.target.value !== originalValueRef.current) {
                        handleSave('meez_email', e.target.value)
                      }
                      originalValueRef.current = null
                    }}
                    disabled={saving === 'meez_email'}
                  />
                </div>
                <div className="max-w-full sm:max-w-none">
                  <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="settings-meez-lookback">
                    Lookback days
                  </label>
                  <input
                    id="settings-meez-lookback"
                    type="number"
                    className="input w-full text-sm"
                    placeholder="7"
                    min="1"
                    max="90"
                    value={getValue('meez_lookback_days')}
                    onFocus={(e) => {
                      originalValueRef.current = e.target.value
                    }}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        meez_lookback_days: { ...prev['meez_lookback_days'], value: e.target.value } as Setting,
                      }))
                    }
                    onBlur={(e) => {
                      if (e.target.value !== originalValueRef.current) {
                        handleSave('meez_lookback_days', e.target.value)
                      }
                      originalValueRef.current = null
                    }}
                    disabled={saving === 'meez_lookback_days'}
                  />
                </div>
              </div>
            </ForwardingIntegrationCard>
          </div>
        </SettingsSection>

      <SettingsSection
        title="Amazon Business"
        icon={amazonSectionIcon}
        detail={
          <>
            <p>
              Configure the QuickBooks credit card account and vendor name used when Amazon Business reconciliation
              report charges post as card expenses or card credits in Doc Review.
            </p>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
          <div className="min-w-0">
            <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="settings-amazon-card-account">
              Card account
            </label>
            <select
              id="settings-amazon-card-account"
              className="input select w-full text-sm"
              value={savedAmazonCardId}
              onChange={(e) => handleSave('amazon_card_account_id', e.target.value)}
              disabled={saving === 'amazon_card_account_id'}
            >
              <option value="">Select credit card account…</option>
              {savedAmazonCardId &&
              !creditCardAccounts.some((c) => c.id === savedAmazonCardId) ? (
                <option value={savedAmazonCardId}>{savedAmazonCardId} (saved)</option>
              ) : null}
              {creditCardAccounts.map((acct) => (
                <option key={acct.id} value={acct.id}>
                  {acct.name}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-0">
            <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="settings-amazon-vendor-name">
              Vendor name
            </label>
            <input
              id="settings-amazon-vendor-name"
              type="text"
              className="input w-full text-sm"
              placeholder="Amazon Business"
              value={getValue('amazon_qbo_vendor_name')}
              onFocus={(e) => {
                originalValueRef.current = e.target.value
              }}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  amazon_qbo_vendor_name: {
                    ...prev['amazon_qbo_vendor_name'],
                    value: e.target.value,
                  } as Setting,
                }))
              }
              onBlur={(e) => {
                if (e.target.value !== originalValueRef.current) {
                  handleSave('amazon_qbo_vendor_name', e.target.value)
                }
                originalValueRef.current = null
              }}
              disabled={saving === 'amazon_qbo_vendor_name'}
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Daily summary email"
        icon={dailySummarySectionIcon}
        detail={
          <>
            <p>
              Sends a once-a-day email summarizing recent invoice activity to the recipients you configure (when the
              server job is enabled).
            </p>
            <p>
              Set <strong>App URL</strong> to the base URL of this deployment so links inside the digest open the
              correct site.
            </p>
            <p>
              <strong>Send as</strong> is the Workspace user identity used to send the message when your domain requires
              delegated sending.
            </p>
          </>
        }
      >
        <div className="min-w-0 space-y-6">
          <SettingRow label="Send digest" badge={onBadge(isActive('daily_digest_enabled'))}>
            <label className="toggle">
              <input
                type="checkbox"
                checked={isActive('daily_digest_enabled')}
                onChange={(e) => handleSave('daily_digest_enabled', e.target.checked ? 'true' : 'false')}
                disabled={saving === 'daily_digest_enabled'}
              />
              <span className="toggle-slider" />
            </label>
          </SettingRow>

          {isActive('daily_digest_enabled') && (
            <div className="grid grid-cols-1 gap-8 border-t border-gray-100 pt-6 lg:grid-cols-2 lg:items-start lg:gap-10">
              <div className="min-w-0 space-y-5 lg:pr-2">
                <div className="space-y-4">
                  <div className="min-w-0">
                    <label
                      className="mb-1.5 block text-xs font-medium text-gray-600"
                      htmlFor="daily-digest-from-name"
                    >
                      From name
                    </label>
                    <input
                      id="daily-digest-from-name"
                      type="text"
                      className="input w-full text-sm"
                      placeholder="Bookkeeper"
                      value={getValue('daily_digest_from_name')}
                      onFocus={(e) => {
                        originalValueRef.current = e.target.value
                      }}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          daily_digest_from_name: {
                            ...prev['daily_digest_from_name'],
                            value: e.target.value,
                          } as Setting,
                        }))
                      }
                      onBlur={(e) => {
                        if (e.target.value !== originalValueRef.current) {
                          handleSave('daily_digest_from_name', e.target.value)
                        }
                        originalValueRef.current = null
                      }}
                      disabled={saving === 'daily_digest_from_name'}
                    />
                  </div>
                  <div className="min-w-0">
                    <label
                      className="mb-1.5 block text-xs font-medium text-gray-600"
                      htmlFor="daily-digest-send-as"
                    >
                      Send as (Workspace user)
                    </label>
                    <input
                      id="daily-digest-send-as"
                      type="email"
                      className="input w-full text-sm"
                      placeholder="noreply@company.com"
                      value={getValue('daily_digest_send_as')}
                      onFocus={(e) => {
                        originalValueRef.current = e.target.value
                      }}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          daily_digest_send_as: {
                            ...prev['daily_digest_send_as'],
                            value: e.target.value,
                          } as Setting,
                        }))
                      }
                      onBlur={(e) => {
                        if (e.target.value !== originalValueRef.current) {
                          handleSave('daily_digest_send_as', e.target.value)
                        }
                        originalValueRef.current = null
                      }}
                      disabled={saving === 'daily_digest_send_as'}
                    />
                  </div>
                </div>
              </div>

              <div className="min-w-0 space-y-5 border-t border-gray-100 pt-6 lg:border-l lg:border-t-0 lg:border-gray-200 lg:pl-10 lg:pt-0">
                <div>
                  <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                    <label className="text-xs font-medium text-gray-600" htmlFor="daily-digest-to">
                      Recipients
                    </label>
                    <HelpDetailPopover aria-label="About digest recipients" align="start">
                      <p>
                        Comma-separated addresses that should receive the digest. Leave blank to use the default from
                        the server environment variable{' '}
                        <code className="text-xs text-gray-700">DAILY_DIGEST_TO</code>.
                      </p>
                    </HelpDetailPopover>
                  </div>
                  <input
                    id="daily-digest-to"
                    type="text"
                    className="input w-full text-sm"
                    placeholder="a@company.com, b@company.com"
                    value={getValue('daily_digest_to')}
                    onFocus={(e) => {
                      originalValueRef.current = e.target.value
                    }}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        daily_digest_to: { ...prev['daily_digest_to'], value: e.target.value } as Setting,
                      }))
                    }
                    onBlur={(e) => {
                      if (e.target.value !== originalValueRef.current) {
                        handleSave('daily_digest_to', e.target.value)
                      }
                      originalValueRef.current = null
                    }}
                    disabled={saving === 'daily_digest_to'}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="app-base-url">
                    App URL (for links in the email)
                  </label>
                  <input
                    id="app-base-url"
                    type="url"
                    className="input w-full text-sm font-mono text-[13px]"
                    placeholder="https://bookkeeper.company.com"
                    value={getValue('app_base_url')}
                    onFocus={(e) => {
                      originalValueRef.current = e.target.value
                    }}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        app_base_url: { ...prev['app_base_url'], value: e.target.value } as Setting,
                      }))
                    }
                    onBlur={(e) => {
                      if (e.target.value !== originalValueRef.current) {
                        handleSave('app_base_url', e.target.value)
                      }
                      originalValueRef.current = null
                    }}
                    disabled={saving === 'app_base_url'}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </SettingsSection>

    </div>
  )
}
